import { CSSObject } from '@mui/system';

import { ButtonHierarchy, ButtonIcon, ButtonSize } from './button.types';
import { COLOR_TOKENS } from '../colors/colors';
import { SHADOWS } from '../shadows/shadows';
import { RADIUS, SPACING } from '../spacing/spacing';
import { TYPOGRAPHY_VARIANTS } from '../typography/typography';

const COMMON_BUTTON_SIZE_STYLES: CSSObject = {
  height: 'fit-content',
  minWidth: 'unset',
};

export const BUTTON_SIZES: Record<ButtonSize, Record<ButtonIcon, CSSObject>> = {
  sm: {
    Default: {
      padding: `${SPACING['spacing-md']} ${SPACING['spacing-lg']}`,
      gap: SPACING['spacing-xs'],
      borderRadius: RADIUS['radius-md'],
      ...COMMON_BUTTON_SIZE_STYLES,
      ...TYPOGRAPHY_VARIANTS['text-sm-semibold'],
    },
    Leading: {
      padding: `${SPACING['spacing-md']} 14px`,
      gap: SPACING['spacing-md'],
      borderRadius: RADIUS['radius-md'],
      ...COMMON_BUTTON_SIZE_STYLES,
      ...TYPOGRAPHY_VARIANTS['text-sm-semibold'],
    },
    Only: {
      padding: SPACING['spacing-md'],
      borderRadius: RADIUS['radius-md'],
      ...COMMON_BUTTON_SIZE_STYLES,
      ...TYPOGRAPHY_VARIANTS['text-sm-semibold'],
    },
  },
  md: {
    Default: {
      padding: '10px 14px',
      gap: SPACING['spacing-xs'],
      borderRadius: RADIUS['radius-md'],
      ...COMMON_BUTTON_SIZE_STYLES,
      ...TYPOGRAPHY_VARIANTS['text-sm-semibold'],
    },
    Leading: {
      padding: `10px ${SPACING['spacing-xl']}`,
      gap: SPACING['spacing-md'],
      borderRadius: RADIUS['radius-md'],
      ...COMMON_BUTTON_SIZE_STYLES,
      ...TYPOGRAPHY_VARIANTS['text-sm-semibold'],
    },
    Only: {
      padding: '10px',
      borderRadius: RADIUS['radius-md'],
      ...COMMON_BUTTON_SIZE_STYLES,
      ...TYPOGRAPHY_VARIANTS['text-sm-semibold'],
    },
  },
  lg: {
    Default: {
      padding: `10px ${SPACING['spacing-xl']}`,
      borderRadius: RADIUS['radius-md'],
      gap: SPACING['spacing-sm'],
      ...COMMON_BUTTON_SIZE_STYLES,
      ...TYPOGRAPHY_VARIANTS['text-md-semibold'],
    },
    Leading: {
      padding: '10px 18px',
      borderRadius: RADIUS['radius-md'],
      gap: SPACING['spacing-md'],
      ...COMMON_BUTTON_SIZE_STYLES,
      ...TYPOGRAPHY_VARIANTS['text-md-semibold'],
    },
    Only: {
      padding: SPACING['spacing-lg'],
      borderRadius: RADIUS['radius-md'],
      ...COMMON_BUTTON_SIZE_STYLES,
      ...TYPOGRAPHY_VARIANTS['text-md-semibold'],
    },
  },
  xl: {
    Default: {
      padding: `${SPACING['spacing-lg']} 18px`,
      borderRadius: RADIUS['radius-md'],
      gap: SPACING['spacing-sm'],
      ...COMMON_BUTTON_SIZE_STYLES,
      ...TYPOGRAPHY_VARIANTS['text-md-semibold'],
    },
    Leading: {
      padding: `${SPACING['spacing-lg']} ${SPACING['spacing-3xl']}`,
      borderRadius: RADIUS['radius-md'],
      gap: SPACING['spacing-md'],
      ...COMMON_BUTTON_SIZE_STYLES,
      ...TYPOGRAPHY_VARIANTS['text-md-semibold'],
    },
    Only: {
      padding: '14px',
      borderRadius: RADIUS['radius-md'],
      ...COMMON_BUTTON_SIZE_STYLES,
      ...TYPOGRAPHY_VARIANTS['text-md-semibold'],
    },
  },
  '2xl': {
    Default: {
      padding: `${SPACING['spacing-xl']} 22px`,
      borderRadius: RADIUS['radius-lg'],
      gap: SPACING['spacing-md'],
      ...COMMON_BUTTON_SIZE_STYLES,
      ...TYPOGRAPHY_VARIANTS['text-lg-semibold'],
    },
    Leading: {
      padding: `${SPACING['spacing-xl']} ${SPACING['spacing-3xl']}`,
      borderRadius: RADIUS['radius-lg'],
      gap: SPACING['spacing-lg'],
      ...COMMON_BUTTON_SIZE_STYLES,
      ...TYPOGRAPHY_VARIANTS['text-lg-semibold'],
    },
    Only: {
      padding: SPACING['spacing-xl'],
      borderRadius: RADIUS['radius-lg'],
      ...COMMON_BUTTON_SIZE_STYLES,
      ...TYPOGRAPHY_VARIANTS['text-lg-semibold'],
    },
  },
};

const COMMON_HIERARCHY_STYLES: CSSObject = {
  fontFamily: 'Sarabun, sans-serif',
  transition: 'all 0.3s ease-in-out',
  textTransform: 'none',
  '& .MuiButton-startIcon': { margin: 0 },
  '& .MuiButton-endIcon': { margin: 0 },
  '&.Mui-disabled': {
    backgroundColor: COLOR_TOKENS['Colors/Background/bg-disabled'],
    color: COLOR_TOKENS['Colors/Foreground/fg-disabled'],
    border: `1px solid ${COLOR_TOKENS['Colors/Border/border-disabled-subtle']}`,
    boxShadow: 'none',
    cursor: 'not-allowed',
    pointerEvents: 'none',
    opacity: '1 !important',
  },
};

export const BUTTON_HIERARCHY_STYLES: Record<ButtonHierarchy, CSSObject> = {
  Primary: {
    ...COMMON_HIERARCHY_STYLES,
    backgroundColor: COLOR_TOKENS['Brand/500'],
    color: COLOR_TOKENS['Colors/Text/text-white'],
    border: `2px solid ${COLOR_TOKENS['Colors/Base/Black']}`,
    '&:hover': {
      boxShadow: SHADOWS['shadow-xl'],
      backgroundColor: COLOR_TOKENS['Brand/500'],
      color: COLOR_TOKENS['Colors/Text/text-white'],
      transform: 'translate(-2px, -2px)',
    },
    '&:active': {
      outline: 'none',
      border: `2px solid ${COLOR_TOKENS['Colors/Base/White']}`,
      boxShadow: '0px 0px 0px 2px black', // update to DS when complete
      transform: 'translate(0px, 0px)',
      transition: 'all 0s',
    },
  },
  'Secondary gray': {
    ...COMMON_HIERARCHY_STYLES,
    backgroundColor: COLOR_TOKENS['Colors/Base/White'],
    color: COLOR_TOKENS['Colors/Text/text-secondary'],
    border: `2px solid ${COLOR_TOKENS['Colors/Border/border-primary']}`,
    position: 'relative',
    '&:hover': {
      backgroundColor: COLOR_TOKENS['Gray/50'],
      color: COLOR_TOKENS['Gray/700'],
    },
    '&:active': {
      outline: 'none',
      border: `2px solid ${COLOR_TOKENS['Colors/Border/border-primary']}`,
      backgroundColor: COLOR_TOKENS['Colors/Base/White'],
      transition: 'all 0s',
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: '-4px',
        borderRadius: 'inherit',
        border: `2px solid ${COLOR_TOKENS['Colors/Base/White']}`,
        pointerEvents: 'none',
        zIndex: 1,
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        inset: '-6px',
        borderRadius: RADIUS['radius-lg'],
        border: `3px solid ${COLOR_TOKENS['Brand/500']}`,
        pointerEvents: 'none',
        zIndex: 1,
      },
    },
  },
};
